import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { getAnalyticsTimeseries, getAnalyticsEvents, getAnalyticsQuality, setAnalyticsFilters } from "store/actions/analytics";
import AnalyticsFilters from "./components/AnalyticsFilters";
import MetricCard from "./MetricCard";
import styles from "./analyticsStyle";
import { CircularProgress, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ChartistGraph from "react-chartist";
import "chartist/dist/chartist.min.css";


const METRIC_LABELS = {
  "ga4.active_users": "Active Users (GA4)",
  "ga4.new_users": "New Users (GA4)",
  "ga4.sessions": "Sessions (GA4)",
  "ga4.engaged_sessions": "Engaged Sessions (GA4)",
  "apple.first_time_downloads": "First Time Downloads (Apple)",
  "apple.total_downloads": "Total Downloads (Apple)",
  "google_play.first_time_installers": "First Time Installers (Google Play)",
  "google_play.install_events": "Install Events (Google Play)",
  "google_play.uninstall_events": "Uninstall Events (Google Play)",
  "internal.new_registered_users": "New Registered Users",
  "internal.recycling_requests": "Recycling Requests",
  "internal.completed_dropoffs": "Completed Drop-offs",
  "internal.collected_items": "Items Collected",
  "internal.points_issued": "Points Issued",
  "internal.vouchers_issued": "Vouchers Issued",
  "internal.vouchers_redeemed": "Vouchers Redeemed",
  "play_vitals.crash_rate": "Crash Rate (Play Vitals)",
  "play_vitals.anr_rate": "ANR Rate (Play Vitals)",
  "play_vitals.crash_count": "Crash Count (Play Vitals)",
  "play_vitals.anr_count": "ANR Count (Play Vitals)",
};

const CHART_COLORS = {
  total: "#6366f1",
  ios: "#f59e0b",
  android: "#10b981",
};

const METRICS_LIST = [
  "ga4.active_users", "ga4.new_users", "ga4.sessions", "ga4.engaged_sessions",
  "apple.first_time_downloads", "apple.total_downloads",
  "google_play.first_time_installers", "google_play.install_events", "google_play.uninstall_events",
  "internal.new_registered_users", "internal.recycling_requests", "internal.completed_dropoffs", "internal.collected_items",
  "internal.points_issued", "internal.vouchers_issued", "internal.vouchers_redeemed",
  "play_vitals.crash_rate", "play_vitals.anr_rate", "play_vitals.crash_count", "play_vitals.anr_count"
];

/**
 * Compute the start/end date range from the current filter selection,
 * then generate date buckets based on the selected granularity.
 */
function getDateRange(analyticsFilters) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let start, end;

  switch (analyticsFilters.period) {
    case "today":
      start = new Date(today);
      end = new Date(today);
      break;
    case "7d":
      end = new Date(today);
      start = new Date(today);
      start.setDate(start.getDate() - 6);
      break;
    case "1m":
      end = new Date(today);
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      break;
    case "6m":
      end = new Date(today);
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      break;
    case "custom":
      if (analyticsFilters.from && analyticsFilters.to) {
        start = new Date(analyticsFilters.from + "T00:00:00");
        end = new Date(analyticsFilters.to + "T00:00:00");
      } else {
        return null;
      }
      break;
    default:
      return null;
  }

  const granularity = analyticsFilters.granularity || "day";
  const buckets = [];
  const cursor = new Date(start);

  if (granularity === "month") {
    // Snap to 1st of start month
    cursor.setDate(1);
    while (cursor <= end) {
      buckets.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
  } else if (granularity === "week") {
    // Snap cursor to the Monday on or before start
    const dayOfWeek = cursor.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0
    cursor.setDate(cursor.getDate() - diff);
    while (cursor <= end) {
      buckets.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
  } else {
    // daily
    while (cursor <= end) {
      buckets.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return { buckets, granularity };
}

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Format a date label based on granularity.
 */
function formatBucketLabel(date, granularity) {
  if (granularity === "month") {
    return `${MONTH_SHORT[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`;
  }
  if (granularity === "week") {
    // ISO week-style label: "M/D"
    const endOfWeek = new Date(date);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    return `${date.getMonth()+1}/${date.getDate()}-${endOfWeek.getMonth()+1}/${endOfWeek.getDate()}`;
  }
  return `${date.getMonth()+1}/${date.getDate()}`;
}

/**
 * Format local date as YYYY-MM-DD string without UTC shift.
 */
function toLocalIsoDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get the bucket key for a data-point date based on granularity.
 */
function getBucketKey(dateStr, granularity) {
  if (!dateStr) return "";
  const parts = dateStr.includes("T") ? dateStr.split("T")[0].split("-") : dateStr.split("-");
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  const d = new Date(year, month, day);

  if (granularity === "month") {
    return `${year}-${String(month + 1).padStart(2, "0")}`;
  }
  if (granularity === "week") {
    // Snap to Monday
    const dayOfWeek = d.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    d.setDate(d.getDate() - diff);
  }
  return toLocalIsoDate(d);
}

/**
 * Align data points to the date range buckets.
 * For week/month granularity, values within the same bucket are summed.
 */
function alignPointsToRange(rangeResult, points) {
  if (!rangeResult || !rangeResult.buckets) return null;
  const { buckets, granularity } = rangeResult;

  // Group points into buckets by summing values
  const bucketMap = {};
  (points || []).forEach((p) => {
    const key = getBucketKey(p.date, granularity);
    if (!bucketMap[key]) {
      bucketMap[key] = { value: 0, ios: null, android: null };
    }
    bucketMap[key].value += (p.value || 0);
    if (p.ios != null) {
      bucketMap[key].ios = (bucketMap[key].ios || 0) + p.ios;
    }
    if (p.android != null) {
      bucketMap[key].android = (bucketMap[key].android || 0) + p.android;
    }
  });

  const labels = [];
  const alignedPoints = [];

  buckets.forEach((d) => {
    let key;
    if (granularity === "month") {
      key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    } else {
      key = toLocalIsoDate(d);
    }
    labels.push(formatBucketLabel(d, granularity));
    alignedPoints.push(bucketMap[key] || { date: key, value: 0, ios: null, android: null });
  });

  return { labels, alignedPoints };
}

export default function AnalyticsCharts() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const history = useHistory();
  const { timeseries, events, quality, analyticsFilters, analyticsLoading } = useSelector((s) => s.analytics);

  const [activeTab, setActiveTab] = useState("timeseries");
  const [selectedMetrics, setSelectedMetrics] = useState(["ga4.active_users", "internal.completed_dropoffs"]);
  const [eventsPlatform, setEventsPlatform] = useState(""); // empty = all
  
  const handleFilterChange = (key, val) => {
    dispatch(setAnalyticsFilters({ ...analyticsFilters, [key]: val }));
  };

  const fetchTab = () => {
    if (activeTab === "timeseries") {
      dispatch(getAnalyticsTimeseries({ ...analyticsFilters, metrics: selectedMetrics.join(",") }, history));
    } else if (activeTab === "events") {
      dispatch(getAnalyticsEvents({ ...analyticsFilters, platform: eventsPlatform }, history));
    } else if (activeTab === "quality") {
      dispatch(getAnalyticsQuality(analyticsFilters, history));
    }
  };

  useEffect(() => {
    if (analyticsFilters.period !== "custom") {
      fetchTab();
    } else if (analyticsFilters.from && analyticsFilters.to) {
      fetchTab();
    }
  }, [activeTab, analyticsFilters, eventsPlatform]);

  const toggleMetric = (m) => {
    if (selectedMetrics.includes(m)) {
      setSelectedMetrics(selectedMetrics.filter(x => x !== m));
    } else {
      setSelectedMetrics([...selectedMetrics, m]);
    }
  };

  const renderTimeseriesTab = () => {
    const sData = timeseries?.data || timeseries;
    const series = Array.isArray(sData?.series) ? sData.series : [];
    const dateRange = getDateRange(analyticsFilters);
    
    return (
      <Box>
        <div className={classes.checkboxGroup}>
          {METRICS_LIST.map(m => (
            <label key={m} className={classes.checkboxLabel}>
              <input type="checkbox" className={classes.checkboxInput} checked={selectedMetrics.includes(m)} onChange={() => toggleMetric(m)} />
              {METRIC_LABELS[m] || m}
            </label>
          ))}
          <button className={classes.refreshBtn} onClick={() => fetchTab()} style={{ marginLeft: "auto" }}>Apply Metrics</button>
        </div>

        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={3}>
          {series.map((s, idx) => {
            const rawPoints = s.points || [];
            
            // Align points to the selected date range, or fall back to raw points
            let labels, displayPoints;
            if (dateRange && dateRange.buckets && dateRange.buckets.length > 0) {
              const aligned = alignPointsToRange(dateRange, rawPoints);
              labels = aligned.labels;
              displayPoints = aligned.alignedPoints;
            } else {
              displayPoints = rawPoints;
              labels = rawPoints.map(p => {
                const d = new Date(p.date);
                return `${d.getMonth()+1}/${d.getDate()}`;
              });
            }

            const hasPlatformBreakdown = displayPoints.some(p => p.ios != null || p.android != null);

            let chartSeries;
            let legendItems;

            if (hasPlatformBreakdown) {
              chartSeries = [
                displayPoints.map(p => p.value),
                displayPoints.map(p => p.ios),
                displayPoints.map(p => p.android),
              ];
              legendItems = [
                { label: "Total", color: CHART_COLORS.total },
                { label: "iOS", color: CHART_COLORS.ios },
                { label: "Android", color: CHART_COLORS.android },
              ];
            } else {
              chartSeries = [displayPoints.map(p => p.value)];
              legendItems = [{ label: "Total", color: CHART_COLORS.total }];
            }

            const minSpacing = dateRange?.granularity === "week" ? 75 : (dateRange?.granularity === "month" ? 60 : 45);
            const minChartWidth = Math.max(labels.length * minSpacing, 360);
            const isScrollable = labels.length > 8;

            const data = { labels, series: chartSeries };
            const options = {
              lineSmooth: true,
              showArea: hasPlatformBreakdown ? false : true,
              fullWidth: true,
              chartPadding: { right: 20, left: 10, top: 10, bottom: 5 },
              axisX: { showGrid: false, showLabel: true },
              axisY: {
                onlyInteger: true,
                labelInterpolationFnc: (value) => (Number.isInteger(value) ? value : Math.round(value))
              }
            };
            
            return (
              <Box key={idx} p={2} style={{ background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", borderRadius: "14px", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box style={{ backgroundColor: "rgba(236,72,153,0.12)", borderRadius: "50%", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", color: "#ec4899" }}>
                    <ShowChartIcon />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, color: isDark ? "#fff" : "#333" }}>{METRIC_LABELS[s.metric_key] || METRIC_LABELS[s.key] || s.key}</Typography>
                    <Typography variant="caption" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>{s.aggregation || "value"} • {s.unit}</Typography>
                  </Box>
                  {s.availability && s.availability.is_partial && (
                    <span className={`${classes.availBadge} ${classes.availPartial}`}>Partial</span>
                  )}
                </Box>
                {hasPlatformBreakdown && (
                  <Box display="flex" gap={2} mb={1} ml={1}>
                    {legendItems.map(item => (
                      <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
                        <Box style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color }} />
                        <Typography variant="caption" style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", fontSize: "0.7rem" }}>{item.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {isScrollable && (
                  <Box display="flex" justifyContent="flex-end" mb={0.5} pr={1}>
                    <Typography variant="caption" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: "0.7rem" }}>
                      ⇄ Scroll to view all {labels.length} dates
                    </Typography>
                  </Box>
                )}
                <div className={classes.chartScrollContainer}>
                  <div style={{ minWidth: isScrollable ? `${minChartWidth}px` : "100%", height: "250px" }}>
                    <ChartistGraph
                      data={data}
                      options={options}
                      type="Line"
                      style={{ height: "250px", width: "100%" }}
                      listener={{
                        created: (ctx) => {
                          if (hasPlatformBreakdown) {
                            const seriesElements = ctx.svg._node.querySelectorAll(".ct-series");
                            const colors = [CHART_COLORS.total, CHART_COLORS.ios, CHART_COLORS.android];
                            seriesElements.forEach((el, i) => {
                              if (colors[i]) {
                                const line = el.querySelector(".ct-line");
                                const dots = el.querySelectorAll(".ct-point");
                                if (line) line.style.stroke = colors[i];
                                dots.forEach(dot => { dot.style.stroke = colors[i]; });
                              }
                            });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </Box>
            );
          })}
          {series.length === 0 && <Typography style={{ color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>No series selected or available.</Typography>}
        </Box>
      </Box>
    );
  };

  const renderEventsTab = () => {
    const eData = events?.data || events || {};
    const evs = eData.events || [];
    const allowlist = eData.configured_allowlist || [];
    
    // Merge allowlist with events to show missing ones as "No Data"
    const displayEvents = allowlist.map(al => {
      const found = evs.find(e => e.event_name === al);
      return found ? found : { event_name: al, event_count: null, user_count: null };
    });
    
    // Add any events that came back but weren't in allowlist (if any)
    evs.forEach(e => {
      if (!displayEvents.find(de => de.event_name === e.event_name)) {
        displayEvents.push(e);
      }
    });

    return (
      <Box>
        <Box display="flex" gap={2} mb={2}>
          <select 
            value={eventsPlatform}
            onChange={(e) => setEventsPlatform(e.target.value)}
            className={classes.inputField}
          >
            <option value="">All Platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
        </Box>

        <TableContainer component={Paper} style={{ background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Event Name</TableCell>
                <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Event Count</TableCell>
                <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>User Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayEvents.map((row, index) => (
                <TableRow key={index}>
                  <TableCell style={{ color: row.event_count === null ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)") : (isDark ? "#fff" : "#333") }}>
                    {row.event_name}
                  </TableCell>
                  <TableCell align="right" style={{ color: row.event_count === null ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)") : (isDark ? "#fff" : "#333") }}>
                    {row.event_count === null ? "No Data" : row.event_count}
                  </TableCell>
                  <TableCell align="right" style={{ color: row.user_count === null ? (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)") : (isDark ? "#fff" : "#333") }}>
                    {row.user_count === null ? "No Data" : row.user_count}
                  </TableCell>
                </TableRow>
              ))}
              {displayEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No events found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderQualityTab = () => {
    const qData = quality?.data || quality || {};
    const dateRange = getDateRange(analyticsFilters);
    
    return (
      <Box>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={3} mb={4}>
          <MetricCard name="Crash Rate" value={qData.crash_rate != null ? parseFloat((qData.crash_rate * 100).toFixed(2)) : null} unit="%" />
          <MetricCard name="ANR Rate" value={qData.anr_rate != null ? parseFloat((qData.anr_rate * 100).toFixed(2)) : null} unit="%" />
          <MetricCard name="Crash Count" value={qData.crash_count} unit="crashes" availability={qData.availability?.crash_count} />
          <MetricCard name="ANR Count" value={qData.anr_count} unit="ANRs" availability={qData.availability?.anr_count} />
        </Box>
        
        {Array.isArray(qData.series) && qData.series.length > 0 && (
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={3}>
            {qData.series.map((s, idx) => {
              const rawPoints = s.points || [];
              
              let labels, displayPoints;
              if (dateRange && dateRange.buckets && dateRange.buckets.length > 0) {
                const aligned = alignPointsToRange(dateRange, rawPoints);
                labels = aligned.labels;
                displayPoints = aligned.alignedPoints;
              } else {
                displayPoints = rawPoints;
                labels = rawPoints.map(p => {
                  const d = new Date(p.date);
                  return `${d.getMonth()+1}/${d.getDate()}`;
                });
              }

              const minSpacing = dateRange?.granularity === "week" ? 75 : (dateRange?.granularity === "month" ? 60 : 45);
              const minChartWidth = Math.max(labels.length * minSpacing, 360);
              const isScrollable = labels.length > 8;

              const data = {
                labels,
                series: [displayPoints.map(p => p.value)]
              };
              const options = {
                lineSmooth: true,
                showArea: true,
                fullWidth: true,
                chartPadding: { right: 20, left: 10, top: 10, bottom: 5 },
                axisX: { showGrid: false, showLabel: true },
                axisY: {
                  onlyInteger: true,
                  labelInterpolationFnc: (value) => (Number.isInteger(value) ? value : Math.round(value))
                }
              };
              
              return (
                <Box key={idx} p={2} style={{ background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", borderRadius: "14px", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)" }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: isDark ? "#fff" : "#333", marginBottom: 16 }}>{s.key}</Typography>
                  {isScrollable && (
                    <Box display="flex" justifyContent="flex-end" mb={0.5} pr={1}>
                      <Typography variant="caption" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: "0.7rem" }}>
                        ⇄ Scroll to view all {labels.length} dates
                      </Typography>
                    </Box>
                  )}
                  <div className={classes.chartScrollContainer}>
                    <div style={{ minWidth: isScrollable ? `${minChartWidth}px` : "100%", height: "250px" }}>
                      <ChartistGraph data={data} options={options} type="Line" style={{ height: "250px", width: "100%" }} />
                    </div>
                  </div>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <div className={classes.pageWrapper}>
      {/* ── Control Bar ── */}
      <AnalyticsFilters classes={classes} onRefresh={fetchTab} showGranularity={true} />

      <div className={classes.tabsContainer}>
        <button className={`${classes.tabButton} ${activeTab === "timeseries" ? classes.tabButtonActive : ""}`} onClick={() => setActiveTab("timeseries")}>Timeseries Charts</button>
        <button className={`${classes.tabButton} ${activeTab === "events" ? classes.tabButtonActive : ""}`} onClick={() => setActiveTab("events")}>Firebase Events</button>
        <button className={`${classes.tabButton} ${activeTab === "quality" ? classes.tabButtonActive : ""}`} onClick={() => setActiveTab("quality")}>Android Quality</button>
      </div>
      
      {analyticsLoading ? (
        <div className={classes.loadingOverlay}><CircularProgress size={32} sx={{ color: "#6366f1", mr: 1.5 }} /> Loading data...</div>
      ) : (
        <Box>
          {activeTab === "timeseries" && renderTimeseriesTab()}
          {activeTab === "events" && renderEventsTab()}
          {activeTab === "quality" && renderQualityTab()}
        </Box>
      )}
    </div>
  );
}
